import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Upload, FileSpreadsheet, Check, X, Loader2,
  ChevronLeft, ChevronRight, Eye, AlertCircle,
} from 'lucide-react';

const ITEMS_PER_PAGE = 5;

/* ---------- Helpers ---------- */
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (amount) => {
  if (amount == null) return '—';
  return Number(amount).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const toYYYYMMDD = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr.replace(/[^0-9]/g, '').slice(0, 8);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
};

/* ---------- Facturas Modal ---------- */
const FacturasModal = ({ fecha, onClose }) => {
  const { token, API_URL } = useAuth();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFacturas = async () => {
      setLoading(true);
      setError('');
      try {
        const fechaParam = toYYYYMMDD(fecha);
        const res = await fetch(`${API_URL}/contabilidad/estado-remesas/${fechaParam}/facturas`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Error al cargar facturas');
        const data = await res.json();
        setFacturas(data.facturas || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchFacturas();
  }, [fecha, token, API_URL]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">
            Facturas de la Remesa: {formatDate(fecha)}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32 gap-2 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Cargando facturas...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          ) : facturas.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No se encontraron facturas para esta remesa</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Factura</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Importe</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Razón Social</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IBAN</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Cobro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {facturas.map((f, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{f.num_factura}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(f.importe)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{f.razon_social}</td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-700">{f.iban}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(f.fecha_cobro)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Pagination ---------- */
const Pagination = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const pages = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-center gap-2 py-4">
      {visiblePages[0] > 1 && (
        <button
          onClick={() => onPageChange(1)}
          className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
        >
          Principio
        </button>
      )}
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {visiblePages.map((p) => (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          className={`px-3 py-1.5 text-sm rounded-lg transition-colors font-medium ${
            p === page
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          {p}
        </button>
      ))}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="p-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50 transition-colors"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ========== ESTADO REMESAS ========== */
const EstadoRemesas = () => {
  const { token, API_URL } = useAuth();

  // Remesas state
  const [remesas, setRemesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadError, setUploadError] = useState('');

  // Modal state
  const [modalFecha, setModalFecha] = useState(null);

  /* ---------- Fetch remesas ---------- */
  const fetchRemesas = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/contabilidad/estado-remesas?page=${p}&limit=${ITEMS_PER_PAGE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Error al cargar remesas');
      const data = await res.json();
      setRemesas(data.remesas || []);
      setTotalPages(data.totalPages || 1);
      setPage(data.page || p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL]);

  useEffect(() => {
    fetchRemesas(1);
  }, [fetchRemesas]);

  /* ---------- Upload ---------- */
  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setUploadMessage(null);
    setUploadError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadMessage(null);
    setUploadError('');

    const formData = new FormData();
    formData.append('archivo', file);

    try {
      const res = await fetch(`${API_URL}/contabilidad/estado-remesas/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al subir el archivo');
      }
      const data = await res.json();
      setUploadMessage(data.message || `Se importaron ${data.count} registros correctamente.`);
      setFile(null);
      // Reset file input
      const input = document.getElementById('remesa-file-input');
      if (input) input.value = '';
      fetchRemesas(1);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  /* ---------- Row color ---------- */
  const getRowClasses = (remesa) => {
    if (remesa.estado === 'match') return 'bg-green-50 hover:bg-green-100/50';
    if (Number(remesa.importe) < 0) return 'bg-red-50 hover:bg-red-100/50';
    return 'hover:bg-blue-50/50';
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <h1 className="text-2xl font-bold text-gray-800">Consulta Estado de Remesas</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Top cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Instructions Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                Instrucciones
              </h2>
            </div>
            <div className="px-6 py-4">
              <ol className="list-decimal list-inside space-y-2 text-gray-700 text-sm">
                <li>Descargar Fichero del Banco.</li>
                <li>Formatos permitidos (.xls .xlsx .xml).</li>
                <li>Cargar fichero para su lectura.</li>
              </ol>
            </div>
          </div>

          {/* Upload Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Subir Documento
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3">
              <input
                id="remesa-file-input"
                type="file"
                accept=".xls,.xlsx,.xlsm,.xlsb,.xlt,.xml"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
                disabled={uploading}
              />
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {uploading ? 'Subiendo...' : 'Subir Remesas'}
              </button>

              {uploadMessage && (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <span>{uploadMessage}</span>
                </div>
              )}
              {uploadError && (
                <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{uploadError}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Remesas Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Cargando remesas...</span>
            </div>
          ) : remesas.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No hay remesas registradas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Contable</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Valor</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {remesas.map((remesa, idx) => (
                    <tr key={idx} className={`transition-colors ${getRowClasses(remesa)}`}>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={remesa.descripcion}>
                        {remesa.descripcion}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(remesa.fecha_contable)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(remesa.fecha_valor)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(remesa.importe)}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setModalFecha(remesa.fecha_contable)}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-1.5"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Facturas
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="border-t border-gray-200">
            <Pagination page={page} totalPages={totalPages} onPageChange={fetchRemesas} />
          </div>
        </div>
      </div>

      {/* Facturas Modal */}
      {modalFecha && (
        <FacturasModal fecha={modalFecha} onClose={() => setModalFecha(null)} />
      )}
    </div>
  );
};

export default EstadoRemesas;
